import React, { useState, useEffect } from 'react';
import { X, Save, Upload, Store } from 'lucide-react';
import '../../styles/marketplace.css';
import { marketplaceAPI } from '../../services/marketplaceAPI';

const StoreSettingsModal = ({ isOpen, onClose, providerId = 'current_user', onUpdate }) => {
    if (!isOpen) return null;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        image: '',
        banner: '',
        currency: 'SAR' // Default
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [availableCurrencies, setAvailableCurrencies] = useState(['SAR', 'USD', 'EUR']); // Fallback + API

    useEffect(() => {
        if (isOpen) {
            setLoading(true);

            // Fetch Provider Details AND Currencies
            const fetchData = async () => {
                try {
                    const [detailsRes, ratesRes] = await Promise.all([
                        marketplaceAPI.getProviderDetails(providerId),
                        marketplaceAPI.getExchangeRates()
                    ]);

                    if (detailsRes.status === 'success') {
                        setFormData({
                            name: detailsRes.data.name || '',
                            description: detailsRes.data.description || '',
                            image: detailsRes.data.image || '',
                            banner: detailsRes.data.banner || '',
                            currency: detailsRes.data.currency || 'SAR'
                        });
                    }

                    if (ratesRes.status === 'success' && ratesRes.rates) {
                        const currencies = Object.keys(ratesRes.rates);
                        setAvailableCurrencies(currencies.length > 0 ? currencies : ['SAR', 'USD']);
                    }
                } catch (error) {
                    console.error("Error fetching settings:", error);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }
    }, [isOpen, providerId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await marketplaceAPI.updateProviderDetails(providerId, formData);
            if (res.status === 'success') {
                onUpdate && onUpdate(formData); // Notify parent
                onClose();
            }
        } catch (error) {
            console.error("Failed to update store:", error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div className="mp-modal-overlay" style={{ zIndex: 11000 }}>
            <div
                className="mp-modal-content"
                style={{ maxWidth: '600px', padding: 0, overflow: 'hidden' }}
            >
                {/* Header */}
                <div className="mp-modal-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Store size={24} color="#F97316" /> {/* Provider Orange */}
                        Store Settings
                    </h2>
                    <button type="button" onClick={onClose} className="mp-modal-close">
                        <X size={24} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="mp-modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        {/* Store Name */}
                        <div className="mp-form-group">
                            <label className="mp-form-label">Store Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="mp-form-input"
                                placeholder="e.g. Happy Pets Shop"
                            />
                        </div>

                        {/* Bio */}
                        <div className="mp-form-group">
                            <label className="mp-form-label">About Your Store</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className="mp-form-textarea"
                                rows="4"
                                placeholder="Tell customers what makes your shop special..."
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        {/* Currency Selector - Dynamic */}
                        <div className="mp-form-group">
                            <label className="mp-form-label">Store Currency</label>
                            <select
                                name="currency"
                                value={formData.currency}
                                onChange={handleChange}
                                className="mp-form-select"
                            >
                                {availableCurrencies.map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                This currency will be used for all your products and sales stats.
                            </p>
                        </div>

                        {/* Images Simulation */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="mp-form-group">
                                <label className="mp-form-label">Logo URL</label>
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleChange}
                                    className="mp-form-input"
                                    placeholder="https://..."
                                />
                                {formData.image && (
                                    <div style={{ marginTop: '8px', border: '1px solid #E5E7EB', padding: '4px', borderRadius: '50%', width: 'fit-content' }}>
                                        <img src={formData.image} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} onError={(e) => e.target.style.display = 'none'} />
                                    </div>
                                )}
                            </div>

                            <div className="mp-form-group">
                                <label className="mp-form-label">Banner URL</label>
                                <input
                                    type="text"
                                    name="banner"
                                    value={formData.banner}
                                    onChange={handleChange}
                                    className="mp-form-input"
                                    placeholder="https://..."
                                />
                                {formData.banner && (
                                    <div style={{ marginTop: '8px', border: '1px solid #E5E7EB', padding: '4px', borderRadius: '8px' }}>
                                        <img src={formData.banner} alt="Banner Preview" style={{ width: '100%', height: '60px', borderRadius: '4px', objectFit: 'cover' }} onError={(e) => e.target.style.display = 'none'} />
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Footer - Fixed at bottom */}
                <div className="mp-modal-footer" style={{ marginTop: 'auto' }}>
                    <button type="button" onClick={onClose} className="mp-btn-secondary">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={saving} className="mp-btn-primary">
                        {saving ? 'Saving...' : 'Save Changes'}
                        {!saving && <Save size={18} />}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default StoreSettingsModal;
